/**
 * Marks-library import logic (ra-gbrd.16).
 *
 * Pure helpers shared by every "import marks from a library" source — the
 * bundled official-buoy library and cross-club/season reuse. Classifies each
 * candidate mark against what the season already has so the picker can show an
 * honest summary (new / already-present / position-pending) and only ever
 * imports marks that carry a real position.
 */
import { normalizeMark } from './markNormalize';

/** Case-insensitive id key (ra-or1y: marks are keyed by exact-case id elsewhere,
 *  so dedup must be case-insensitive to avoid silent duplicate aids). */
function idKey(mark) {
  return String(mark?.id ?? '').toUpperCase();
}

function hasFinitePosition(mark) {
  return Number.isFinite(mark?.latitude) && Number.isFinite(mark?.longitude);
}

/**
 * Classify candidate marks against the marks already in the season.
 *
 * @param {Array} candidates - library/source marks (may carry `positionPending`)
 * @param {Array} existing - marks already present in the season
 * @returns {{
 *   items: Array<{ mark: object, status: 'new'|'existing'|'pending' }>,
 *   total: number, newCount: number, existingCount: number, pendingCount: number,
 * }}
 */
export function summarizeLibraryImport(candidates, existing) {
  const existingKeys = new Set((existing || []).map(idKey));
  const items = (candidates || []).map((raw) => {
    const mark = normalizeMark(raw);
    let status;
    if (existingKeys.has(idKey(mark))) {
      status = 'existing';
    } else if (raw?.positionPending || !hasFinitePosition(mark)) {
      status = 'pending';
    } else {
      status = 'new';
    }
    return { mark, status };
  });
  return {
    items,
    total: items.length,
    newCount: items.filter((i) => i.status === 'new').length,
    existingCount: items.filter((i) => i.status === 'existing').length,
    pendingCount: items.filter((i) => i.status === 'pending').length,
  };
}

/**
 * Given the summary and the set of selected ids, return the storable marks to
 * write. Only `new` (importable, real-position) marks are ever returned — a
 * selected `existing` or `pending` id is silently skipped so the caller can
 * never persist a position-less aid or clobber an existing one.
 *
 * @param {ReturnType<typeof summarizeLibraryImport>} summary
 * @param {Set<string>|Array<string>} selectedIds
 * @returns {Array} storable marks
 */
export function pickImportableMarks(summary, selectedIds) {
  const selected = selectedIds instanceof Set ? selectedIds : new Set(selectedIds || []);
  return summary.items
    .filter((i) => i.status === 'new' && selected.has(String(i.mark.id)))
    .map((i) => i.mark);
}

/** All importable (status 'new') ids — convenience for a "select all" default. */
export function importableIds(summary) {
  return summary.items.filter((i) => i.status === 'new').map((i) => String(i.mark.id));
}

/**
 * Strip library-only metadata, returning a mark in the exact shape the committee
 * stores. Library sources (the live Irish Lights feed, cross-club reuse) tag
 * candidates with provenance (`source`, `sourceUrl`) and a `positionPending`
 * flag; none of that belongs on a stored season mark. Only importable marks
 * (real position) should reach this.
 */
export function toStorableMark(libraryMark) {
  const {
    positionPending, // eslint-disable-line no-unused-vars
    source, // eslint-disable-line no-unused-vars
    sourceUrl, // eslint-disable-line no-unused-vars
    ...rest
  } = libraryMark;
  return rest;
}
